import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import AdminConfirmDialog from "./AdminConfirmDialog";
import BulkActionBar from "./BulkActionBar";
import { Search, ChevronDown, MoreVertical, ShieldOff, ShieldCheck, Eye } from "lucide-react";

const TIERS = ["all", "free", "adventure", "premium"];
const ROLES = ["all", "user", "moderator", "admin"];
const STATUSES = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "suspended", label: "Suspended" },
];
const SORTS = [
    { id: "newest", label: "Newest" },
    { id: "oldest", label: "Oldest" },
    { id: "name", label: "Name A-Z" },
];

export default function UsersTab({ currentAdminId }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [q, setQ] = useState("");
    const [tier, setTier] = useState("all");
    const [role, setRole] = useState("all");
    const [status, setStatus] = useState("all");
    const [sort, setSort] = useState("newest");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // { user, change: 'tier'|'role'|'suspend'|'restore', newValue? }
    const [selected, setSelected] = useState([]);
    const [bulkConfirm, setBulkConfirm] = useState(null); // { action, title, requireReason, danger }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users", {
                params: { q: q.trim(), tier, role, status, sort, page, page_size: pageSize },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, tier, role, status, sort, page, pageSize]);

    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    useEffect(() => { setPage(1); }, [q, tier, role, status, sort]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const onConfirmEdit = async (reason) => {
        if (!editing) return;
        const { user, change, newValue } = editing;
        try {
            if (change === "tier") {
                await api.patch(`/admin/users/${user.user_id}`, { tier: newValue, reason });
                toast.success(`Tier changed → ${newValue}`);
            } else if (change === "role") {
                await api.patch(`/admin/users/${user.user_id}`, { role: newValue, reason });
                toast.success(`Role changed → ${newValue}`);
            } else if (change === "suspend") {
                await api.post(`/admin/users/${user.user_id}/suspend`, { reason });
                toast.success("User suspended");
            } else if (change === "restore") {
                await api.post(`/admin/users/${user.user_id}/restore`);
                toast.success("User restored");
            }
            setEditing(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((u) => u.user_id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const runBulk = async (action, reason) => {
        try {
            const { data } = await api.post(`/admin/users/bulk?action=${action}`, {
                ids: selected,
                reason,
            });
            toast.success(`${data.affected} user(s) updated`);
            setBulkConfirm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((u) => selected.includes(u.user_id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "user_id", label: "User ID" },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "tier", label: "Tier" },
                { key: "role", label: "Role" },
                { key: "suspended", label: "Suspended", map: (r) => (r.suspended ? "yes" : "") },
                { key: "created_at", label: "Joined" },
            ],
            "users"
        );
    };

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="bg-snowcap border border-ridge-line rounded-lg p-4 space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                    <input
                        data-testid="admin-users-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, email, username, or user ID…"
                        className="w-full pl-10 pr-4 py-2.5 rounded-md bg-alpine-mist border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 text-sm"
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    <FilterSelect label="Tier" value={tier} onChange={setTier} options={TIERS} testid="admin-users-filter-tier" />
                    <FilterSelect label="Role" value={role} onChange={setRole} options={ROLES} testid="admin-users-filter-role" />
                    <FilterSelect
                        label="Status"
                        value={status}
                        onChange={setStatus}
                        options={STATUSES}
                        testid="admin-users-filter-status"
                    />
                    <FilterSelect label="Sort" value={sort} onChange={setSort} options={SORTS} testid="admin-users-filter-sort" />
                    <a
                        href={`${api.defaults.baseURL}/admin/users/export.csv`}
                        target="_blank"
                        rel="noopener"
                        data-testid="admin-users-compliance-export"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-midnight-pine text-midnight-pine text-xs font-bold hover:bg-midnight-pine hover:text-white transition-colors"
                        title="Includes terms_version, terms_accepted_at, signup_method"
                    >
                        Compliance CSV
                    </a>
                </div>
            </div>

            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="admin-users-bulk"
                    actions={[
                        { id: "suspend", label: "Suspend", danger: true, onClick: () => setBulkConfirm({ action: "suspend", title: `Suspend ${selected.length} user(s)?`, danger: true, requireReason: true }) },
                        { id: "restore", label: "Restore", onClick: () => setBulkConfirm({ action: "restore", title: `Restore ${selected.length} user(s)?`, requireReason: false }) },
                    ]}
                />
            )}

            {/* Table */}
            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-alpine-mist">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                <th className="py-3 pl-4 w-10">
                                    <input
                                        type="checkbox"
                                        data-testid="admin-users-select-all-cb"
                                        checked={items.length > 0 && selected.length === items.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className="py-3 px-4">Name</th>
                                <th className="py-3 px-4">Email</th>
                                <th className="py-3 px-4">Tier</th>
                                <th className="py-3 px-4">Role</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Joined</th>
                                <th className="py-3 px-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="border-t border-ridge-line">
                                        <td colSpan={8} className="py-3 px-4">
                                            <div className="h-5 bg-alpine-mist rounded animate-pulse" />
                                        </td>
                                    </tr>
                                ))
                            ) : items.length === 0 ? (
                                <tr><td colSpan={8} className="py-12 text-center text-slate-gray">No users match these filters.</td></tr>
                            ) : (
                                items.map((u) => (
                                    <UserRow
                                        key={u.user_id}
                                        u={u}
                                        currentAdminId={currentAdminId}
                                        selected={selected.includes(u.user_id)}
                                        onToggleSelect={() => toggleOne(u.user_id)}
                                        onEdit={(change, newValue) => setEditing({ user: u, change, newValue })}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between p-4 border-t border-ridge-line">
                    <span className="text-xs text-slate-gray font-semibold">
                        Showing {items.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + items.length} of {total}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="admin-users-prev"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                        >
                            Prev
                        </button>
                        <span className="text-xs font-bold text-granite">
                            {page} / {totalPages}
                        </span>
                        <button
                            data-testid="admin-users-next"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            <AdminConfirmDialog
                open={!!editing}
                onClose={() => setEditing(null)}
                onConfirm={onConfirmEdit}
                title={
                    editing?.change === "suspend"
                        ? `Suspend ${editing?.user?.name}?`
                        : editing?.change === "restore"
                        ? `Restore ${editing?.user?.name}?`
                        : editing?.change === "tier"
                        ? `Change tier to "${editing?.newValue}"?`
                        : editing?.change === "role"
                        ? `Change role to "${editing?.newValue}"?`
                        : ""
                }
                description={
                    editing?.change === "suspend"
                        ? "They'll be logged out immediately and blocked from logging in until restored."
                        : editing?.change === "restore"
                        ? "Their account will work normally again."
                        : "Change is logged in the audit trail with your reason."
                }
                confirmLabel={editing?.change === "suspend" ? "Suspend" : editing?.change === "restore" ? "Restore" : "Save"}
                danger={editing?.change === "suspend"}
                requireReason={editing?.change !== "restore"}
            />

            <AdminConfirmDialog
                open={!!bulkConfirm}
                onClose={() => setBulkConfirm(null)}
                onConfirm={(reason) => runBulk(bulkConfirm.action, reason)}
                title={bulkConfirm?.title || ""}
                description="Applies to every selected row. Logged to the audit trail."
                confirmLabel="Apply"
                danger={bulkConfirm?.danger}
                requireReason={bulkConfirm?.requireReason}
            />
        </div>
    );
}

function UserRow({ u, currentAdminId, selected, onToggleSelect, onEdit }) {
    const [open, setOpen] = useState(false);
    const isSelf = u.user_id === currentAdminId;
    const isSuspended = u.suspended === true;

    return (
        <tr
            data-testid={`admin-user-row-${u.user_id}`}
            className={`border-t border-ridge-line hover:bg-alpine-mist/40 ${isSuspended ? "bg-ember-orange/5" : ""}`}
        >
            <td className="py-3 pl-4">
                <input
                    type="checkbox"
                    data-testid={`admin-user-cb-${u.user_id}`}
                    checked={!!selected}
                    onChange={onToggleSelect}
                    disabled={isSelf}
                />
            </td>
            <td className="py-3 px-4 font-semibold text-granite">{u.name}</td>
            <td className="py-3 px-4 text-slate-gray truncate max-w-[200px]">{u.email}</td>
            <td className="py-3 px-4">
                <TierMenu
                    user={u}
                    onChange={(t) => onEdit("tier", t)}
                />
            </td>
            <td className="py-3 px-4">
                <RoleMenu
                    user={u}
                    isSelf={isSelf}
                    onChange={(r) => onEdit("role", r)}
                />
            </td>
            <td className="py-3 px-4">
                {isSuspended ? (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-ember-orange/20 text-ember-dark">
                        Suspended
                    </span>
                ) : (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-deep-evergreen/15 text-deep-evergreen">
                        Active
                    </span>
                )}
            </td>
            <td className="py-3 px-4 text-xs text-slate-gray">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
            </td>
            <td className="py-3 px-4 text-right relative">
                <button
                    data-testid={`admin-user-actions-${u.user_id}`}
                    onClick={() => setOpen((v) => !v)}
                    className="p-1.5 rounded-md hover:bg-alpine-mist"
                >
                    <MoreVertical className="w-4 h-4 text-slate-gray" />
                </button>
                {open && (
                    <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                        <div className="absolute right-4 top-10 z-20 bg-snowcap border border-ridge-line rounded-md shadow-lg w-48 py-1 text-left">
                            <Link
                                to={`/profile/${u.user_id}`}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-granite hover:bg-alpine-mist"
                            >
                                <Eye className="w-3.5 h-3.5" /> View profile
                            </Link>
                            {isSuspended ? (
                                <button
                                    data-testid={`admin-user-restore-${u.user_id}`}
                                    onClick={() => { setOpen(false); onEdit("restore"); }}
                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-semibold text-deep-evergreen hover:bg-alpine-mist"
                                >
                                    <ShieldCheck className="w-3.5 h-3.5" /> Restore user
                                </button>
                            ) : (
                                <button
                                    data-testid={`admin-user-suspend-${u.user_id}`}
                                    onClick={() => { setOpen(false); onEdit("suspend"); }}
                                    disabled={isSelf}
                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ember-dark hover:bg-alpine-mist disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ShieldOff className="w-3.5 h-3.5" /> Suspend user
                                </button>
                            )}
                        </div>
                    </>
                )}
            </td>
        </tr>
    );
}

function TierMenu({ user, onChange }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                data-testid={`admin-user-tier-${user.user_id}`}
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-ridge-line text-xs font-bold uppercase tracking-widest hover:border-midnight-pine capitalize"
            >
                {user.tier} <ChevronDown className="w-3 h-3" />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 top-full mt-1 z-20 bg-snowcap border border-ridge-line rounded-md shadow-lg w-32 py-1">
                        {["free", "adventure", "premium"].map((t) => (
                            <button
                                key={t}
                                data-testid={`admin-user-tier-${user.user_id}-${t}`}
                                onClick={() => { setOpen(false); if (t !== user.tier) onChange(t); }}
                                className={`w-full text-left px-3 py-1.5 text-xs font-semibold capitalize hover:bg-alpine-mist ${
                                    t === user.tier ? "text-ember-orange" : "text-granite"
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function RoleMenu({ user, isSelf, onChange }) {
    const [open, setOpen] = useState(false);
    const roles = ["user", "moderator", "admin"];
    return (
        <div className="relative">
            <button
                data-testid={`admin-user-role-${user.user_id}`}
                onClick={() => setOpen((v) => !v)}
                disabled={isSelf}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-ridge-line text-xs font-bold uppercase tracking-widest hover:border-midnight-pine capitalize disabled:opacity-50"
            >
                {user.role} <ChevronDown className="w-3 h-3" />
            </button>
            {open && !isSelf && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 top-full mt-1 z-20 bg-snowcap border border-ridge-line rounded-md shadow-lg w-32 py-1">
                        {roles.map((r) => (
                            <button
                                key={r}
                                data-testid={`admin-user-role-${user.user_id}-${r}`}
                                onClick={() => { setOpen(false); if (r !== user.role) onChange(r); }}
                                className={`w-full text-left px-3 py-1.5 text-xs font-semibold capitalize hover:bg-alpine-mist ${
                                    r === user.role ? "text-ember-orange" : "text-granite"
                                }`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    const opts = options.map((o) => (typeof o === "string" ? { id: o, label: o } : o));
    return (
        <label className="flex items-center gap-2 text-xs">
            <span className="font-bold uppercase tracking-widest text-slate-gray">{label}</span>
            <select
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="px-3 py-1.5 rounded-md bg-snowcap border border-ridge-line text-sm font-semibold text-granite focus:border-ember-orange focus:outline-none capitalize"
            >
                {opts.map((o) => (
                    <option key={o.id} value={o.id} className="capitalize">{o.label}</option>
                ))}
            </select>
        </label>
    );
}
